import { Component } from 'react'
import PropTypes from 'prop-types'
import Aperture from 'react-feather/dist/icons/aperture.js'

import FormGlobalErrors from '../../Form/FormGlobalErrors.jsx'
import Loader from '../../../../shared/components/Loader/Loader.jsx'
import UploadDropZone from '../../Form/Field/UploadDropZone.jsx'
import ConfirmModal from '../../../../shared/components/Modals/ConfirmModal/ConfirmModal.jsx'

import {toastr} from 'react-redux-toastr'

import conf from '../../../conf.js'
import upload from './submit.js'
import api from '../../../../api.js'
import {isUserFreelance} from "../../../../shared/services/userService"

export default class UploadCvForm extends Component {
  constructor(props) {
    super(props)
    this.state = {
      uploading: false,
      errors: null,
      isModalOpen: false,
      confirmModalMode: 'upload', // 'upload' | 'direct' — conservé pendant l'animation de fermeture
      fileData: null,
    }
  }

  static propTypes = {
    freelanceId: PropTypes.number.isRequired,
    cv: PropTypes.object,
    onSubmitSuccess: PropTypes.func,
    onUploadSuccess: PropTypes.func,
    history: PropTypes.object.isRequired,
    freelance: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    currentUser: PropTypes.object,
    redirectionUrl: PropTypes.string,
    showAiConsent: PropTypes.bool,
  }

  static defaultProps = {
    className: '',
    cv: null,
    onSubmitSuccess: () => {},
    onUploadSuccess: () => {},
    redirectionUrl: null,
    currentUser: null,
    showAiConsent: true,
  }

  handleAccept = async () => {
    if (this.state.confirmModalMode === 'direct') {
      await this.doDirectAnalysis()
    } else {
      await this.doUpload(true)
    }
  }

  handleRefuse = async () => {
    await this.doUpload(false)
  }

  isTimeoutError = (e) => e && [408, 504, 524].includes(e.status)

  doDirectAnalysis = async () => {
    this.setState({ isModalOpen: false, uploading: "Traitement des données en cours...", errors: null })
    try {
      const response = await api.freelances.ai({ freelance: this.props.freelanceId })
      if (response.data && response.data.status === 'success') {
        let redirectPath = this.props.redirectionUrl
        if (!redirectPath) {
          if (isUserFreelance(this.props.currentUser)) {
            redirectPath = `/edit/freelance/cv-comparison`
          } else {
            redirectPath = `/freelance/${this.props.freelance.id}/cv-data-comparison`
          }
        }
        this.props.history.push({
          pathname: redirectPath,
          state: {
            analyse: response.data,
            freelance: this.props.freelance,
            user: this.props.user
          }
        })
      }
    } catch (e) {
      console.warn(e)
      if (this.isTimeoutError(e)) {
        toastr.error(
          'Délai d\'analyse dépassé',
          `Essayez avec un fichier moins volumineux. Si le problème persiste, contactez l'administrateur : ${conf.site.emails.contact}`
        )
        return
      }
      this.setState({ errors: e.errors })
    } finally {
      this.setState({ uploading: false })
    }
  }

  doUpload = async (useAI) => {
    this.setState({ isModalOpen: false, uploading: useAI ? "Traitement des données en cours..." : "Upload en cours...", errors: null })
    try {
      const cvResult = await upload({ freelance: this.props.freelanceId, useAI }, { body: this.state.fileData })

      if (useAI && cvResult.data.analyse) {
        if (cvResult.data.analyse.status === 'error') {
          toastr.error(cvResult.data.analyse.error)
          this.setState({ fileData: null })
          return
        }

        let redirectPath = this.props.redirectionUrl

        if (!redirectPath) {
          if (isUserFreelance(this.props.currentUser)) {
            redirectPath = `/edit/freelance/cv-comparison`
          } else {
            redirectPath = `/freelance/${this.props.freelance.id}/cv-data-comparison`
          }
        }

        this.props.history.push({
          pathname: redirectPath,
          state: {
            analyse: cvResult.data.analyse,
            freelance: this.props.freelance,
            user: this.props.user
          }
        })
        return
      }

      this.props.onUploadSuccess(cvResult.data)
      this.props.onSubmitSuccess()
    } catch (e) {
      console.warn(e)
      if (useAI && this.isTimeoutError(e)) {
        toastr.error(
          'Délai d\'analyse dépassé',
          `Essayez avec un fichier moins volumineux. Si le problème persiste, contactez l'administrateur : ${conf.site.emails.contact}`
        )
        this.setState({ fileData: null })
        return
      }
      this.setState({ errors: e.errors })
    } finally {
      this.setState({ uploading: false })
    }
  }

  onSubmit = (data) => {
    const isFreelanceProfile = isUserFreelance(this.props.user)
    if (isFreelanceProfile && this.props.showAiConsent) {
      this.setState({ fileData: data, isModalOpen: true, confirmModalMode: 'upload' })
    } else {
      this.setState({ fileData: data }, () => this.doUpload(false))
    }
  }

  render() {
    const { cv, user, showAiConsent } = this.props
    const { isModalOpen, confirmModalMode } = this.state
    const isFreelanceProfile = isUserFreelance(user)
    const showAnalysisButton = cv && isFreelanceProfile && showAiConsent

    return (
      <div className={this.props.className}>
        <FormGlobalErrors errors={this.state.errors}/>
        <UploadDropZone
          className="cv-dropzone"
          input={{value: cv, name: 'uploadedFile'}}
          dropText="Déposer mon fichier en Drag'n'Drop"
          uploadText="Sélectionner mon fichier"
          uploadModifyText="Modifier mon fichier"
          onSubmit={this.onSubmit}
          maxSize={conf.server.uploadMaxSize}
          acceptText=".doc, .docx, .pdf"
          accept={conf.allowedMimeTypes.cv}
          extraActions={showAnalysisButton && (
            <button
              type="button"
              className="btn btn-secondary mx-1"
              onClick={() => this.setState({ isModalOpen: true, confirmModalMode: 'direct' })}
            >
              <Aperture size={14} style={{ marginRight: 6 }} />
              Analyse intelligente
            </button>
          )}
        />

        {cv ?
          <div className="mt-1 text-center">
            <a
              href={cv.original}
              onClick={(e) => e.stopPropagation()}
              target="_blank" rel="noopener noreferrer">Cliquez ici
            </a> pour visualiser le fichier envoyé ({cv.name})
          </div> : ''
        }

        <Loader visible={this.state.uploading}>{ this.state.uploading }</Loader>

        <ConfirmModal
            isOpen={isModalOpen}
            header="Analyse intelligente de votre fichier"
            body={`<p>
              <strong>Gagnez du temps :</strong> en important votre fichier, vous acceptez que son contenu soit <strong>analysé automatiquement par une IA</strong> pour structurer et enrichir votre profil en quelques secondes.
              <ul>
                <li><strong>Confidentialité :</strong> Vos données sont traitées exclusivement en <strong>France</strong> via des solutions sécurisées (<strong>Mistral AI</strong>).</li>
                <li><strong>Éthique :</strong> Aucune revente de données. Utilisation strictement limitée à la création de votre profil.</li>
                <li><strong>Contrôle :</strong> Vous gardez un droit de suppression total à tout moment.</li>
              </ul>
            </p>
            <div class="callout callout-warning">
              <p>L'analyse peut prendre jusqu'à 2 minutes si le fichier contient beaucoup d'informations ou une mise en page particulière.</p>
            </div>`}
            confirmText="J'accepte l'analyse"
            cancelText={confirmModalMode === 'direct' ? 'Annuler' : 'Saisir manuellement'}
            confirmAction={this.handleAccept}
            onCancel={confirmModalMode === 'direct'
              ? () => this.setState({ isModalOpen: false })
              : this.handleRefuse
            }
            closeModal={() => this.setState({ isModalOpen: false })}
        />
      </div>
    )
  }
}
